import { flatten } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { black, Color, darkGrey, mediumGrey, grey, pickingFilterHighlightColor, RGBA, white } from '@methanesat/colors';
import { GeoJsonLayer, MaskExtension, MultiMVTLayer, ParsedMvtTile, TextLayer, Tile2DHeader } from '@methanesat/maps';
import { useTheme } from '@methanesat/ui-components';

import {
    basinHighlightColor,
    maxZoomOGITiles,
    pipelineHighlightColor,
    pointInfrastructureHighlightColor,
    ZOOM_THRESHOLDS
} from '../../../consts';
import {
    selectEmissionsMapInfraCategoryFilter,
    selectEmissionsMapLayerConfigs,
    selectEmissionsMapName,
    selectFeatureSelected,
    selectInfrastructureTileLayerConfig,
    selectMethaneLayerConfig,
    selectMethaneProduct,
    selectPlatform,
    setOGILayerConfig
} from '../../../reducers';
import {
    ClusteredPointOGITileFeature,
    isBasinOGITileFeature,
    isClusteredPointOGITileFeature,
    isLineOGITileFeature,
    isPointOGITileFeature,
    NothingHighlighted,
    OGILayerIds,
    PolygonOGITileFeature,
    PointOGITileFeature,
    MethaneLayerIds,
    GoogleBasemapNames
} from '../../../types';
import type { RootState } from '../../../store';
import { cleanInfraCount, fetchForLayers, getCollectionNamesForLayer } from '../../../utils';
import { useGetStacSearchQuery } from '../../../reducers/api';

/**
 * Builds the deck.gl layer for infrastructure
 */
export const useInfrastructureTileLayer = () => {
    const theme = useTheme();
    const prefersDarkMode: boolean = theme.palette.mode === 'dark';
    const dispatch = useDispatch();
    const product = useSelector(selectMethaneProduct);
    const platform = useSelector(selectPlatform);
    const collectionIds = useMemo(() => getCollectionNamesForLayer(product, platform), [product, platform]);
    const { data: allCaptures } = useGetStacSearchQuery({ stacSearch: { collections: collectionIds, limit: 1000 } });
    const selectedHighlightedFeatureId = useSelector(
        (state: RootState) => selectEmissionsMapLayerConfigs(state)[OGILayerIds.tileInfrastructure].highlightedFeatureId
    );
    const selectedPointInfrastructureObject = useSelector(
        (state: RootState) => selectFeatureSelected(state).selectedOGIFeature.object
    );

    const hoveredPlumeId = useSelector(
        (state: RootState) => selectMethaneLayerConfig(state)[MethaneLayerIds.plumeEmissionRate].hoveredPlumeId
    );

    const [hoveredObjectId, setHoveredObjectId] = useState(null);
    const isHoveredItem = (d: GeoJSON.Feature) => {
        // don't highlight if a plume hover is active
        if (hoveredPlumeId !== null) return false;
        //prevent double highlight on selected feature
        if (d.id === highlightedFeatureId) {
            return false;
        }
        return d.id === hoveredObjectId;
    };

    // return feature color based on if it is selected, hovered over, or base state
    const getFeatureColor = (d: GeoJSON.Feature, baseColor: Color, highlightColor: Color) => {
        return d.id === highlightedFeatureId ||
            (Array.isArray(highlightedFeatureId) && highlightedFeatureId.includes(d.id as string))
            ? highlightColor
            : isHoveredItem(d)
              ? highlightColor
              : baseColor;
    };

    // function to set redux state from within MultiMVTileLayer when it finishes loading data in the viewport
    const onViewportLoad = useCallback(
        (tiles: Tile2DHeader[]) => {
            // get all features from all tiles, remove falsy features
            const data = flatten(tiles.map((tile) => tile.content).filter((data) => !!data));
            const highlightedObject =
                selectedPointInfrastructureObject ||
                data.find((feature: GeoJSON.Feature) => feature.id === selectedHighlightedFeatureId) ||
                null;
            dispatch(
                setOGILayerConfig({
                    layerId: OGILayerIds.tileInfrastructure,
                    layerUpdates: { highlightedObject: highlightedObject }
                })
            );
        },
        [dispatch, selectedHighlightedFeatureId, selectedPointInfrastructureObject]
    );
    const getPointRadius = (d: PointOGITileFeature | ClusteredPointOGITileFeature) =>
        isClusteredPointOGITileFeature(d) ? 23 : 5;
    const getPointLineWidth = (d: PointOGITileFeature | ClusteredPointOGITileFeature) =>
        isClusteredPointOGITileFeature(d) ? 7 : 1;

    const selectedOgiCategories = useSelector(selectEmissionsMapInfraCategoryFilter);
    const selectedOgiCategoryIds = selectedOgiCategories.map(({ id }) => id);
    const mapName = useSelector(selectEmissionsMapName);

    const {
        basinVisibility,
        categories,
        companyColorByOgim,
        data,
        categoryIds: _categoryIds,
        highlightedFeatureId,
        ogimFeatures,
        pipelineConfig,
        pipelineInfraVisibility,
        pointInfraConfig,
        pointInfraVisibility,
        polygonInfraConfig,
        visible,
        transitions
    } = useSelector(selectInfrastructureTileLayerConfig);

    // Light mode roadmap and satellite base maps use the same styling. For the dark mode roadmap, infrastructure is brightened.
    const useDarkModeColors = mapName !== GoogleBasemapNames.Satellite && prefersDarkMode;
    const basinLineColor = useDarkModeColors ? darkGrey : polygonInfraConfig.getLineColor;
    const pointFillColor = useDarkModeColors ? grey : pointInfraConfig.getFillColor;
    const pointClusterFillColor = useDarkModeColors ? ([...grey, 153] as Color) : pointFillColor;
    const pointLineColor = useDarkModeColors ? darkGrey : pointInfraConfig.getLineColor;
    const pointClusterLineColor = useDarkModeColors ? ([...darkGrey, 153] as Color) : pointLineColor;
    const pipelineLineColor = useDarkModeColors ? mediumGrey : pipelineConfig.getLineColor;
    const pointClusterTextColor = useDarkModeColors ? black : white;
    const pointHighlightColor = useDarkModeColors ? white : pointInfrastructureHighlightColor;
    const selectedPointInfrastructureHighlightObject =
        selectedPointInfrastructureObject && !isClusteredPointOGITileFeature(selectedPointInfrastructureObject)
            ? selectedPointInfrastructureObject
            : [];

    return [
        // STAC items. This layer is used as a mask for all OGI layers. The OGI we display is
        // limited to features that fall at least partly within existing captures.
        new GeoJsonLayer({
            id: 'emissions-mask-layer',
            data: allCaptures,
            operation: 'mask'
        }),
        // This is the main layer for infrastructure tiles. Since different types of OGI must be
        // rendered differently, the data is split by type into sublayers, handled in
        // the `renderSublayers` method.
        new MultiMVTLayer({
            autoHighlight: false,
            binary: false,
            data,
            highlightedFeatureId: Array.isArray(highlightedFeatureId)
                ? NothingHighlighted.highlightedFeatureId
                : highlightedFeatureId,
            id: OGILayerIds.tileInfrastructure,
            minZoom: ZOOM_THRESHOLDS.MINIMUM_ZOOM_LEVEL_BASINS,
            maxZoom: maxZoomOGITiles,
            pickable: true,
            visible,
            fetch: fetchForLayers,
            updateTriggers: {
                renderSublayers: [
                    basinVisibility,
                    categories,
                    pointInfraVisibility,
                    pipelineInfraVisibility,
                    highlightedFeatureId,
                    selectedOgiCategories,
                    hoveredObjectId,
                    hoveredPlumeId
                ]
            },
            onViewportLoad,
            onHover: (info) => {
                if (info.object) {
                    setHoveredObjectId(info.object.id);
                } else {
                    setHoveredObjectId(null);
                }
            },
            renderSubLayers: (props) => {
                // render sublayers for each loaded tile
                const { tile, data } = props;
                if (!data || typeof data === 'string') return null;
                if (!Array.isArray(data)) return null;
                props.autoHighlight = false;

                const pipelineData = (data as ParsedMvtTile)
                    .filter(isLineOGITileFeature)
                    .filter((d) => selectedOgiCategoryIds.includes(d.properties.layerName));
                const pointData = (data as ParsedMvtTile)
                    .filter(isPointOGITileFeature)
                    .filter((d) => selectedOgiCategoryIds.includes(d.properties.layerName));
                const polygonData = data.filter(isBasinOGITileFeature);

                /** note: remove casting `as string` for all color accessors for all layers
                 * once PR https://github.com/visgl/deck.gl/pull/8006 is merged, published &
                 * we update the deck.gl version.
                 */

                // we need to exclude _offset from the props passed
                // to the text layer for clustered points
                const { _offset, ...restSublayerProps } = props;
                return [
                    // basins
                    new GeoJsonLayer<PolygonOGITileFeature>({
                        ...props,
                        data: polygonData,
                        filled: false,
                        getFillColor: polygonInfraConfig.getFillColor,
                        getLineColor: (d) => {
                            // If we are filtering by specific ogim, color them
                            if (ogimFeatures.length > 0) {
                                return companyColorByOgim[d.id];
                            }
                            const isHighlighted =
                                Array.isArray(highlightedFeatureId) && highlightedFeatureId.includes(d.id as string);

                            if (isHighlighted || isHoveredItem(d)) {
                                return pickingFilterHighlightColor(
                                    basinLineColor as RGBA,
                                    basinHighlightColor
                                ) as Color;
                            }

                            return basinLineColor;
                        },
                        getLineWidth: 1000,
                        highlightColor: basinHighlightColor,
                        id: `basin-infrastructure-${tile.id}`,
                        lineWidthMaxPixels: 10,
                        lineWidthMinPixels: 2,
                        visible: basinVisibility,
                        opacity: basinVisibility ? 1 : 0,
                        transitions,
                        updateTriggers: {
                            getLineColor: useDarkModeColors,
                            highlightColor: useDarkModeColors
                        }
                    }),
                    // points
                    new GeoJsonLayer({
                        ...props,
                        data: pointData,
                        getFillColor: (d: PointOGITileFeature | ClusteredPointOGITileFeature) => {
                            const baseColor = isClusteredPointOGITileFeature(d)
                                ? (pointClusterFillColor as Color)
                                : (pointFillColor as Color);

                            // If we are filtering by specific ogim, color them
                            if (ogimFeatures.length > 0) {
                                return companyColorByOgim[d.id];
                            }

                            const highlightColor = pickingFilterHighlightColor(baseColor, pointHighlightColor) as Color;

                            return getFeatureColor(d, baseColor, highlightColor);
                        },
                        getLineColor: (d: PointOGITileFeature | ClusteredPointOGITileFeature) => {
                            const baseColor = isClusteredPointOGITileFeature(d)
                                ? (pointClusterLineColor as Color)
                                : (pointLineColor as Color);

                            // If we are filtering by specific ogim, we color them. This
                            // line makes the different pieces distinguishable
                            if (ogimFeatures.length > 0) {
                                return baseColor;
                            }

                            const highlightColor = pickingFilterHighlightColor(baseColor, pointHighlightColor) as Color;

                            return getFeatureColor(d, baseColor, highlightColor);
                        },
                        getLineWidth: getPointLineWidth,
                        stroked: pointInfraConfig.stroked,
                        getPointRadius,
                        highlightColor: selectedPointInfrastructureObject
                            ? [0, 0, 0, 0]
                            : pointInfrastructureHighlightColor,
                        id: `point-infrastructure-${tile.id}`,
                        lineWidthUnits: pointInfraConfig.lineWidthUnits,
                        pickable: true,
                        pointRadiusUnits: pointInfraConfig.pointRadiusUnits,
                        visible: pointInfraVisibility,
                        extensions: [new MaskExtension()],
                        maskId: 'emissions-mask-layer',
                        maskByInstance: true,
                        opacity: pointInfraVisibility ? 1 : 0,
                        transitions,
                        updateTriggers: {
                            getLineColor: useDarkModeColors,
                            highlightColor: useDarkModeColors
                        }
                    }),
                    // cluster labels
                    new TextLayer({
                        ...restSublayerProps,
                        data: pointData,
                        getPosition: (d) => d.geometry.coordinates,
                        getSize: (d) => (d.properties.point_count >= 1000 ? 12 : 14),
                        getText: (d) => (d.properties.point_count ? cleanInfraCount(d.properties.point_count) : ''),
                        id: `point-infrastructure-text-${tile.id}`,
                        pickable: false,
                        getColor: pointClusterTextColor,
                        highlightColor: [0, 0, 0, 0],
                        visible: pointInfraVisibility,
                        extensions: [new MaskExtension()],
                        maskId: 'emissions-mask-layer',
                        maskByInstance: true,
                        opacity: pointInfraVisibility ? 1 : 0,
                        transitions,
                        updateTriggers: {
                            getColor: useDarkModeColors
                        }
                    }),
                    // pipelines
                    new GeoJsonLayer({
                        ...props,
                        data: pipelineData,
                        getFillColor: pipelineConfig.getFillColor,
                        getLineColor: (d) => {
                            // If we are filtering by specific ogim, color them
                            if (ogimFeatures.length > 0) {
                                return companyColorByOgim[d.id];
                            }

                            const baseColor = pipelineLineColor as Color;
                            const highlightColor = pickingFilterHighlightColor(
                                baseColor,
                                pipelineHighlightColor
                            ) as Color;

                            return getFeatureColor(d, baseColor, highlightColor);
                        },
                        getLineWidth: 100,
                        highlightColor: pipelineHighlightColor,
                        id: `pipeline-infrastructure-${tile.id}`,
                        lineWidthMaxPixels: 12,
                        lineWidthMinPixels: 4,
                        visible: pipelineInfraVisibility,
                        extensions: [new MaskExtension()],
                        maskId: 'emissions-mask-layer',
                        maskByInstance: false,
                        opacity: pipelineInfraVisibility ? 1 : 0,
                        transitions,
                        updateTriggers: {
                            getLineColor: useDarkModeColors,
                            highlightColor: useDarkModeColors
                        }
                    })
                ];
            }
        }),
        // This layer allows us to display infrastructure the user has selected (highlighted) even
        // after the user has zoomed out to the point where the feature may be clustered in with
        // other point infrastructure. It is a layer of a single selected infrastructure object.
        new GeoJsonLayer({
            id: 'point-infrastructure-highlight',
            data: selectedPointInfrastructureHighlightObject,
            stroked: pointInfraConfig.stroked,
            pickable: false,
            getFillColor: () => pickingFilterHighlightColor(pointFillColor as Color, pointHighlightColor),
            getLineColor: () => pickingFilterHighlightColor(pointLineColor as Color, pointHighlightColor),
            getPointRadius,
            getLineWidth: getPointLineWidth,
            pointRadiusUnits: pointInfraConfig.pointRadiusUnits,
            lineWidthUnits: pointInfraConfig.lineWidthUnits,
            visible: pointInfraVisibility,
            updateTriggers: {
                getFillColor: useDarkModeColors,
                getLineColor: useDarkModeColors
            }
        })
    ];
};
